import Vue from 'vue';
import Component from 'vue-class-component';
import VueRecaptcha from 'vue-recaptcha';
import EdFormModel from '@/models/ed-form.model';
import EdInputModel from '@/models/ed-input.model';
import KEYS from '@/values/keys';
import STATES from '@/core/values/states';
import { TOAST_TYPES } from '@/plugins/ed-toast/values/ed-toast.values';
import { required, maxLength, minLength, numeric } from 'vuelidate/lib/validators';
import { CORE_ROUTES } from '@/core/core.router';
import { AUTH_EVENTS, CAPTCHA_KEYS, PUBLIC_RATE_LIMIT_WAIT_SECONDS, VERIFY_TYPES } from '@/auth/values/auth.values';
import EdPasswordProgressBar from '@/auth/components/ed-password-progress-bar/ed-password-progress-bar.vue';
import EdModalAgreementComponent from '@/auth/components/ed-register/ed-modal-agreement/ed-modal-agreement.component.vue'
import EdRegisterValidatePhoneComponent from '@/auth/components/ed-register/ed-register-validate-phone/ed-register-validate-phone.component.vue';

@Component({
  name: 'complete-substitute-teacher-invitation',
  components: {
    VueRecaptcha,
    EdPasswordProgressBar,
    EdRegisterValidatePhoneComponent
  },
})
export default class CompleteSubstituteTeacherInvitationView extends Vue {

  /* Data */
  public form: any = {};
  public saving: boolean = false;
  public cities: any = [];
  public countries: any = [];
  public reCaptcha: boolean = false;
  public reCaptchaKeys: any = CAPTCHA_KEYS;
  public isStrong: boolean = false;
  public differentPass: boolean = false;
  public substituteInvitation: any = {};
  public verifyType: any = null;
  public verifyTypes: any = VERIFY_TYPES;
  public validatedPhone: any = null;
  public requiredPhoneValidation = true; 

  /* Computed */
  get hash() {
    return this.$route.query.invitationId;
  }

  /* Methods */
  private init(firstName = '', lastName = '') {
    this.form = EdFormModel([
      EdInputModel('firstName', firstName, {required, maxLength: maxLength(60)}),
      EdInputModel('middleName', '', {maxLength: maxLength(60)}, true),
      EdInputModel('lastName', lastName, {required, maxLength: maxLength(60)}),
      EdInputModel('country', '', {required}),
      EdInputModel('city', '', {required}),
      EdInputModel('address', '', {required, maxLength: maxLength(200)}),
      EdInputModel('address2', '', {maxLength: maxLength(200)}, true),
      EdInputModel('address3', '', {maxLength: maxLength(200)}, true),
      EdInputModel('phoneNumber', '', {required, maxLength: maxLength(15)}, true),
      EdInputModel('phoneNumberExt', '', {numeric, maxLength: maxLength(7)}, true),
      EdInputModel('phoneVerificationCode', '', {required}),
      EdInputModel('phoneVerificationId', '', {required}),
      EdInputModel('zipcode', '', {required}),
      EdInputModel('state', '', {required}),
      EdInputModel('username', '', {required, maxLength: maxLength(60)}),
      EdInputModel('password', '', {required, maxLength: maxLength(60), minLength: minLength(8)}),
      EdInputModel('confirmPassword', '', { required })
    ]);
  }

  goHome() {
    this.$router.push({ name: CORE_ROUTES.HOME.NAME })
  }

  accept() {
    this.$EdModal.createModal(EdModalAgreementComponent);
  }

  // Confirm terms and save form
  async confirmTerms(termsId) {
    this.saving = true;
    try {
      let data = {
        accountInformation: {
          emailAddress: this.substituteInvitation.email,
          password: this.form.password.value,
          passwordConfirmation: this.form.confirmPassword.value,
          username: this.form.username.value
        },
        applicant: {
          address: {
            address1: this.form.address.value,
            address2: this.form.address2.value,
            address3: this.form.address3.value,
            city: this.form.city.value,
            countryCode: this.form.country.value,
            state: this.form.state.value,
            zipCode: this.form.zipcode.value
          },
          phone: {
            phoneNumber: this.form.phoneNumber.value,
            extension: this.form.phoneNumberExt.value
          },
          firstName: this.form.firstName.value,
          lastName: this.form.lastName.value,
          middleName: this.form.middleName.value,
          verificationPhoneId: this.form.phoneVerificationId.value
        },
        invitationHash: this.hash,
        termsAndConditionsId: termsId,
      }
      await this.$Auth.Services.completeSubstituteTeacherInvitation(data);
      this.saving = false;
      this.goHome();
    } catch (err) {
      this.$EdToast.create({ 
        msg: `${this.$t('auth.register.errors.invitationNoAccepted')}`,
        type: TOAST_TYPES.DANGER,
        top: '5.5rem',
        timeout: 3000
      });
      this.saving = false;
    }
  }

  async findZipcode(zipcode) {
    try {
      let response = await this.$Auth.Services.validateZipcode(zipcode);
      this.form.set('state', STATES.find(el => el.state == response.state).name);
      this.cities = response.cities;
    } catch {
      this.form.set('zipcode', '');
      this.form.set('state', '');
      this.form.set('city', '');
      this.form.setValid('city', false);
      this.cities = [];
      this.$EdModal.createMessageModal(
        {
          title: this.$i18n.t('auth.register.register'),
          type: KEYS.EVENT_TYPES.WARNING,
          body: `${this.$t('auth.register.info.zipcodeNotFound')}`, 
        }
      );
    }
  }

  findUsername(username) {
    this.$Auth.Services.existsUsername(username)
    .then((res) => {
      this.$EdModal.createMessageModal(
        {
          body: this.$t('auth.register.errors.username'), 
          title: this.$i18n.t('auth.register.register'),
          type: KEYS.EVENT_TYPES.WARNING,
          action: async () => {
            this.form.setValid('username', false);
            this.form.set('username', '')
          }
        }
      );
    }).catch((error) => {})
  }

  async findPhone(phoneNumber) {
    try {
      await this.$Auth.Services.existsPhoneNumber(phoneNumber)
      this.$EdModal.createMessageModal(
        {
          body: this.$t('auth.register.errors.phoneAlreadyExists'), 
          title: this.$i18n.t('auth.register.register'),
          type: KEYS.EVENT_TYPES.WARNING,
          action: async () => {
            this.form.setValid('phoneNumber', false);
            this.form.set('phoneNumber', '')
          }
        }
      );

      return false;
    } catch(error) {
      return true;
    }
    
    return true;
  }
  
  public watchIfStrong(isStrong: boolean) {
    this.isStrong = isStrong;    
  }

  public verifyPasswords() {
    this.differentPass = false;
    if (this.form.password.value == '' || this.form.confirmPassword.value == '')
      this.differentPass = false;
    else if (this.form.password.value != this.form.confirmPassword.value) {
      this.differentPass = true;
    }
  }

  async getSubstituteInvitation() {
    try {
      this.substituteInvitation = await this.$Auth.Services.getSubstituteInvitationByHash(this.hash);
      this.init();
    } catch {
      this.$EdToast.create({ 
        msg: `${this.$t('auth.register.errors.invitationExpired')}`,
        type: TOAST_TYPES.DANGER,
        top: '5.5rem',
        timeout: 3000
      });
      setTimeout(() => {
        this.goHome();
      }, 3000)
    }
  }

  async validatePhoneModal() {
    if(this.verifyType != VERIFY_TYPES.TEXT_MESSAGE) {
      this.$EdModal.createMessageModal({
        title: this.$i18n.t('auth.register.headers.phone'),
        body: this.$i18n.t('auth.register.info.mustSelectVerifyMethod'),
        type: KEYS.EVENT_TYPES.WARNING,
      });
      return;
    }
    if(!this.form.phoneNumber.value) {
      this.$EdModal.createMessageModal({
        title: this.$i18n.t('auth.register.headers.phone'),
        body: this.$i18n.t('auth.register.errors.phoneNoValid'),
        type: KEYS.EVENT_TYPES.WARNING,
      });
      return;
    }
    (this.$refs.EdLoading as any).show();
    let validPhone = await this.findPhone(this.form.phoneNumber.value);
    if(validPhone) {
      let response = await this.sendCode();
      response && this.$EdModal.createModal(EdRegisterValidatePhoneComponent, { 
        phone: this.form.phoneNumber.value,
        verificationId: response.verificationId,
        getVerificationResponse: this.getVerificationResponse
      });
    }
    (this.$refs.EdLoading as any).hide();
  }

  public getVerificationResponse(verificationId, code) {
    this.form.set('phoneVerificationCode', code);
    this.form.setValid('phoneVerificationCode', true);
    this.form.set('phoneVerificationId', verificationId);
    this.form.setValid('phoneVerificationId', true);
    this.validatedPhone = this.form.phoneNumber.value;
    this.requiredPhoneValidation = false;
  }

  public async sendCode() {    
    try {
      let response = await this.$Auth.Services.sendPhoneCode(this.form.phoneNumber.value);
      return response
    } catch (error) {
      this.$EdModal.createMessageModal({
        title: this.$i18n.t('auth.register.headers.phone'),
        body: error?.status === 429
          ? this.$i18n.t('auth.register.errors.phoneCodeRateLimited', { seconds: PUBLIC_RATE_LIMIT_WAIT_SECONDS.SMS_VERIFICATION })
          : this.$i18n.t('auth.register.errors.codeNotSent'),
        type: KEYS.EVENT_TYPES.WARNING
      });
      return false
    }
  }

  /* Hooks */
  async created() {
    this.init();
    this.countries = await this.$Auth.Services.getCountries();
    this.countries = this.countries.map(el => { return { text: el.name, value: el.code } });
    this.getSubstituteInvitation();
    this.$edEventBus.$on(AUTH_EVENTS.CONFIRM_TERMS_MODAL, this.confirmTerms);
    !this.form.phoneVerificationCode.value && this.form.setValid('phoneVerificationCode', false);
    !this.form.phoneVerificationId.value && this.form.setValid('phoneVerificationId', false);
  }

  beforeMount () {
    const $root: any = this.$root;
    $root.logout(false)
  }

}
